import React, { Component } from "react"
import { Row, Col, Container } from "react-bootstrap"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import { forkJoin, Subscription } from "rxjs"
import AccountService from "../../api/Administration/Model/AccountService"
import Service from "../../api/Administration/Model/Service"
import ServiceType from "../../api/Administration/Model/ServiceType"
import ServicesGet, { AccountServicesGet, ServiceTypesGet } from "../../api/Administration/OrderableServices"
import { APIError } from "../../api/ObservableFromFetch"
import { AppState, AssignedClinic, WorkforceClinicPortal } from "../../AppState"
import { GetSelectedAssignedClinic, GetSelectedPortal } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import Checkbox from "../Checkbox"

interface SiteServicesAuthProps {
  Company?: AssignedClinic,
  Portal?: WorkforceClinicPortal
}

interface ServiceGroup {
  name: string
  code: string
  services: { name: string, value: string, enabled: boolean }[]
}

export interface SiteServicesProps {
  hidden: boolean,
  services: string[],
  onChange: (services: string[]) => void
}

interface SiteServicesState {
  loadingServices: boolean
  types: ServiceType[]
  services: Service[]
  accountServices: AccountService[],
  serviceGroups: ServiceGroup[]
}

export class SiteServices extends Component<SiteServicesProps & SiteServicesAuthProps, SiteServicesState> {
  emptyState: SiteServicesState = {
    loadingServices: false,
    types: [],
    services: [],
    accountServices: [],
    serviceGroups: []
  }
  state:SiteServicesState = { ...this.emptyState }

  loadSubscription?: Subscription
  loadAccountServicesSubscription?: Subscription

  componentWillMount() {
    this.loadInitialData()
  }

  componentWillUnmount() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.loadAccountServicesSubscription && this.loadAccountServicesSubscription.unsubscribe()
  }

  loadInitialData() {
    if (this.props.hidden) return
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.setState({ loadingServices: true })

    this.loadSubscription = forkJoin({
      types: ServiceTypesGet(),
      services: ServicesGet()
    }).subscribe(
      ({ types, services }) => {
        this.setState({ types: types, services: services })
        this.loadAccountServices(this.props.Portal?.ImplementationID || 0, this.props.Company?.AccountID)
      },
      (error) => this.handleAPIError(error, { loadingServices: false })
    )
  }

  loadAccountServices = (implementationID: number, accountID?: number) => {
    this.loadAccountServicesSubscription && this.loadAccountServicesSubscription.unsubscribe()
    let accountServices: AccountService[] = []
    this.loadAccountServicesSubscription = AccountServicesGet(implementationID, accountID).subscribe(
      data => {
        if (data?.length) {
          accountServices = data
        }
        else {
          if (accountID) {
            this.loadAccountServices(implementationID)
          }
          else {
            accountServices = this.state.services.map(service => {return {AccountServiceID: 0, ImplementationID: implementationID, ServiceID: service.ServiceID, Active: false, IsDefault: false}})
          }
        }
        if (accountServices.length) {
          this.setState({accountServices: accountServices, loadingServices: false})
          this.populateServiceData()
        }
      },
      (error) => this.handleAPIError(error, { loadingServices: false })
    )
  }

  populateServiceData = () => {
    let serviceGroups = this.state.types.map((type) => ({name: type.ServiceTypeName, code: type.ServiceTypeCode, services: this.populateServices(type.ServiceTypeID)}))
    this.setState({ serviceGroups: serviceGroups })
  }


  populateServices = (typeID: number) => {
    return this.state.services.filter(service => service.ServiceTypeID === typeID).map((service) => (
        {name: service.ServiceDescription || service.ServiceName || "", value: service.FFServiceCode, enabled: this.checkEnabled(service.ServiceID)}
      ))
  }

  checkEnabled = (serviceID: number): boolean => {
    const relatedService = this.state.accountServices.find(accService => accService.ServiceID === serviceID)
    const companyMode = !!this.props.Company?.AccountID
    const enabledForPortal = relatedService ? !companyMode && relatedService?.Active : false
    const enabledForCompany = relatedService ? (!!relatedService.AccountID && relatedService?.Active) || (companyMode && !relatedService.AccountID && relatedService.IsDefault) : false
    return enabledForPortal || enabledForCompany
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name
    const services = this.props.services.filter(service => service !== name)
    if (e.target.checked) {
      services.push(name)
    }
    this.props.onChange(services)
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  render() {
    if (this.props.hidden) {
      return <></>
    }

    return (
      <Container fluid className="pt-2" hidden={this.state.loadingServices}>
        <Row><Col className="note-message font-italic px-3">At least one service must be checked to search</Col></Row>
        {this.state.serviceGroups.map(serviceGroup => <Row key={serviceGroup.name}>
          <Col className="pt-2">
            <span className="secondary font-weight-bold">{serviceGroup.name}</span>
            {serviceGroup.services.map((service, index) => <Checkbox key={`${service.name}_${index}`} className="ml-3" label={service.name} id={`${service.value}_id`} name={service.value} checked={this.props.services.includes(service.value)} onChange={this.onChange} disabled={!service.enabled} />)}
          </Col>
        </Row>)}
      </Container>
    )
  }
}

function mapStateToProps(state: AppState): SiteServicesAuthProps {
  return {
    Company: GetSelectedAssignedClinic(state) || undefined,
    Portal: GetSelectedPortal(state)
  }
}
const ConnectedSiteServices = connect(mapStateToProps, null)(SiteServices)
export default ConnectedSiteServices
